// Types
import { Set } from '@globalTypes/store';
import { UnitAndRangeState } from './unitAndRangeStore.types';

/**
 * Returns a function to update the range state of a given `UnitAndRangeState` set.
 *
 * @param {Set<ScheduleState>} set - The state setter function.
 * @returns {(range: [number, number]) => void} A function that accepts a tuple of numbers (range) and updates the state's range and `hasRangePill` properties.
 */
const setRange =
  (set: Set<UnitAndRangeState>) =>
  (range: [number, number]): void =>
    set((state: UnitAndRangeState) => {
      state.range = range;
      state.hasRangePill = false;
    });

/**
 * Returns a function to update the `minValue` and `hasRangePill` properties of a given `UnitAndRangeState` set.
 *
 * @param {Set<ScheduleState>} set - The state setter function.
 * @returns {(minValue?: number) => void} A function that accepts an optional number (minValue) and updates the state's `minValue` and determines the `hasRangePill` status.
 */
const setMinValue =
  (set: Set<UnitAndRangeState>) =>
  (minValue?: number): void =>
    set((state: UnitAndRangeState) => {
      state.minValue = minValue;
      state.hasRangePill =
        (state.range[0] && minValue && state.range[0] !== minValue) ||
        (state.range[1] && state.maxValue && state.range[1] !== state.maxValue) ||
        false;
    });

/**
 * Returns a function to update the `maxValue` and `hasRangePill` properties of a given `UnitAndRangeState` set.
 *
 * @param {Set<ScheduleState>} set - The state setter function.
 * @returns {(maxValue?: number) => void} A function that accepts an optional number (maxValue) and updates the state's `maxValue` and determines the `hasRangePill` status.
 */
const setMaxValue =
  (set: Set<UnitAndRangeState>) =>
  (maxValue?: number): void =>
    set((state: UnitAndRangeState) => {
      state.maxValue = maxValue;
      state.hasRangePill =
        (state.range[0] && state.minValue && state.range[0] !== state.minValue) ||
        (state.range[1] && maxValue && state.range[1] !== maxValue) ||
        false;
    });

/**
 * Returns a function to update the `minValue`, `maxValue`, and `hasRangePill` properties of a given `UnitAndRangeState` set.
 *
 * @param {Set<ScheduleState>} set - The state setter function.
 * @returns {(minValue?: number, maxValue?: number) => void} A function that accepts optional `minValue` and `maxValue` numbers and updates the state accordingly.
 */
const setValues =
  (set: Set<UnitAndRangeState>) =>
  (minValue?: number, maxValue?: number): void =>
    set((state: UnitAndRangeState) => {
      state.minValue = minValue;
      state.maxValue = maxValue;
      state.hasRangePill =
        (state.range[0] && minValue && state.range[0] !== minValue) ||
        (state.range[1] && maxValue && state.range[1] !== maxValue) ||
        false;
    });

/**
 * Returns a function to update the `unit` property of a given `UnitAndRangeState` set.
 *
 * @param {Set<ScheduleState>} set - The state setter function.
 * @returns {(unit: string) => void} A function that accepts a `unit` string and updates the `unit` property of the state accordingly.
 */
const setUnit =
  (set: Set<UnitAndRangeState>) =>
  (unit: string): void =>
    set((state: UnitAndRangeState) => {
      state.unit = unit;
    });

/**
 * Returns a function to update the `options` property of a given `UnitAndRangeState` set.
 *
 * @param {Set<ScheduleState>} set - The state setter function.
 * @returns {(options: string[]) => void} A function that accepts an array of `options` and updates the `options` property of the state accordingly.
 */
const setOptions =
  (set: Set<UnitAndRangeState>) =>
  (options: string[]): void =>
    set((state: UnitAndRangeState) => {
      state.options = options;
    });

/**
 * Resets the provided `UnitAndRangeState` object to its default values.
 *
 * @param {Set<ScheduleState>} set - The state setter function.
 * @returns {() => void} A void function
 */
const clear = (set: Set<UnitAndRangeState>) => (): void =>
  set((state: UnitAndRangeState) => {
    state.unit = 'All';
    state.minValue = undefined;
    state.maxValue = undefined;
    state.range = [undefined, undefined];
    state.hasRangePill = false;
  });

export const unitAndRangeActions = (set: Set<UnitAndRangeState>) => ({
  setRange: setRange(set),
  setMinValue: setMinValue(set),
  setMaxValue: setMaxValue(set),
  setValues: setValues(set),
  setUnit: setUnit(set),
  setOptions: setOptions(set),
  clear: clear(set),
});
